import { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Drawer from "../../../components/ui/Drawer";
import { useToast } from "../../../components/ui/Toast";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useBulkAssignGoalsMutation } from "../../../api/managementApi";
import { SoftButton, cls } from "../../hatim/soft/SoftKit";
import GoalListBuilder, { sectionClass } from "./GoalListBuilder";
import StepDots from "./StepDots";
import { BULK_STEPS, MAX_BULK_MEMBERS, canAdvance, noServerResponse } from "./bulkSteps";
import {
  allVisibleSelected,
  filterMembers,
  initialSelection,
  memberName,
  toSelectable,
  toggleAllVisible,
  toggleMember,
} from "./memberSelection";
import { failedRows, memberChangeText, reasonText, summaryText, unitChangeRows, unitChangeText } from "./previewText";

const STEP_TEXT = {
  goals: { title: "bulk_step_goals", description: "bulk_step_goals_desc" },
  members: { title: "bulk_step_members", description: "bulk_step_members_desc" },
  preview: { title: "bulk_step_preview", description: "bulk_step_preview_desc" },
  result: { title: "bulk_step_result", description: "bulk_step_result_desc" },
};

/** Önizleme ya da sonuç: özet, birimi değişecekler, üye başına değişiklik, eklenemeyenler. */
function Outcome({ data, phase, pickable, t }) {
  const [expanded, setExpanded] = useState([]);
  const rows = phase === "preview" ? unitChangeRows(data) : [];
  const failures = failedRows(data, pickable);
  const members = data.members || [];
  return (
    <div data-bulk-outcome={phase}>
      <div className={`mx-4 mt-1 px-4 py-3 rounded-lg ${cls.chip}`}>
        <p className="text-[15px]" data-testid="bulk-summary">
          {summaryText(data, phase, t)}
        </p>
      </div>

      {rows.length > 0 ? (
        <>
          <h4 className={sectionClass}>{t("bulk_unit_changes")}</h4>
          <ul>
            {rows.map((row) => {
              const open = expanded.includes(row.key);
              return (
                <li key={row.key} className={cls.row}>
                  <button
                    type="button"
                    className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm ${cls.title}`}
                    onClick={() =>
                      setExpanded((keys) => (open ? keys.filter((k) => k !== row.key) : [...keys, row.key]))
                    }
                  >
                    <span className="flex-1 min-w-0">{unitChangeText(row, t)}</span>
                    {open ? (
                      <ExpandLessIcon fontSize="small" className={cls.muted} />
                    ) : (
                      <ExpandMoreIcon fontSize="small" className={cls.muted} />
                    )}
                  </button>
                  {open ? <p className={`px-4 pb-2.5 text-xs ${cls.muted}`}>{row.names.join(", ")}</p> : null}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {members.length > 0 ? (
        <>
          <h4 className={sectionClass}>{t(phase === "preview" ? "bulk_per_member" : "bulk_per_member_done")}</h4>
          <ul>
            {members.map((m) => (
              <li key={m.userId} className={`px-4 py-2.5 ${cls.row}`} data-member={m.userId}>
                <span className={`block text-[15px] truncate ${cls.title}`}>{memberName(m)}</span>
                <span className={`block text-xs mt-0.5 ${cls.muted}`}>{memberChangeText(m, phase, t)}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {failures.length > 0 ? (
        <>
          <h4 className={sectionClass}>{t(phase === "preview" ? "bulk_cannot_add" : "bulk_could_not_add")}</h4>
          <ul>
            {failures.map((f) => (
              <li key={f.userId} className={`px-4 py-2.5 text-sm ${cls.row} ${cls.title}`}>
                {f.name ? `${f.name} · ${reasonText(f.reason, t)}` : reasonText(f.reason, t)}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}

/**
 * Toplu Hedef Ata (mobil bulk-goals): Hedefler, Üyeler, Önizleme, Sonuç.
 * Üstte adım noktaları, altta sabit Geri ve İleri. Önizlemeyi sunucu
 * hesaplar (dryRun); istemci kural hesaplamaz. members: grup verisindeki
 * üyeler ({ id, name, surname }).
 */
export default function BulkGoalsDrawer({ open, groupId, members, onClose }) {
  const { t } = useScriptMode();
  const toast = useToast();
  const [bulkAssign] = useBulkAssignGoalsMutation();

  const pickable = useMemo(() => toSelectable(members), [members]);

  const [stepIndex, setStepIndex] = useState(0);
  const [goals, setGoals] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [result, setResult] = useState(null);
  // Gerçek atama cevapsız kaldı: sunucuda sürüyor olabilir, yeniden Ata yok.
  const [uncertain, setUncertain] = useState(false);
  // Geri dönülüp yeniden girilince eski önizleme cevabı yenisini ezmesin.
  const previewSeq = useRef(0);
  // Aynı anda tek gerçek atama: çift tıklama ikinci isteği göndermesin.
  const inFlight = useRef(false);

  // Her açılış baştan: bütün üyeler seçili, liste boş.
  useEffect(() => {
    if (!open) return;
    previewSeq.current += 1;
    inFlight.current = false;
    setStepIndex(0);
    setGoals([]);
    setTemplateId(null);
    setSelected(initialSelection(pickable));
    setQuery("");
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setAssigning(false);
    setResult(null);
    setUncertain(false);
    // Yalnız açılışta; açıkken üye listesi tazelenince seçim bozulmasın.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const step = BULK_STEPS[stepIndex];
  const text = STEP_TEXT[step];
  const visible = useMemo(() => filterMembers(pickable, query), [pickable, query]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const nobodyAssignable =
    step === "preview" && Boolean(preview) && !previewLoading && (preview.members || []).length === 0;
  const ready =
    canAdvance(step, {
      goalCount: goals.length,
      selectedCount: selected.length,
      previewReady: Boolean(preview) && !previewLoading && (preview.members || []).length > 0,
    }) &&
    !assigning &&
    !uncertain;

  const runPreview = async () => {
    const seq = ++previewSeq.current;
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const res = await bulkAssign({ groupId, memberIds: selected, goals, templateId, dryRun: true }).unwrap();
      if (seq === previewSeq.current) setPreview(res?.data || null);
    } catch (err) {
      if (seq === previewSeq.current) setPreviewError(err?.message || t("bulk_preview_error"));
    } finally {
      if (seq === previewSeq.current) setPreviewLoading(false);
    }
  };

  const assign = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    let keepLocked = false;
    setAssigning(true);
    try {
      const res = await bulkAssign({ groupId, memberIds: selected, goals, templateId }).unwrap();
      setResult(res?.data || null);
      setStepIndex(BULK_STEPS.indexOf("result"));
    } catch (err) {
      if (noServerResponse(err)) {
        // Cevap gelmedi: atama sunucuda tamamlanmış olabilir, ikinci kez gönderilmez.
        keepLocked = true;
        setUncertain(true);
        return;
      }
      // Seçimler yerinde kalır; yeniden denenebilir.
      toast.error(err?.message || t("bulk_assign_error"));
    } finally {
      if (!keepLocked) inFlight.current = false;
      setAssigning(false);
    }
  };

  const next = () => {
    if (!ready) return;
    if (step === "goals") setStepIndex(1);
    else if (step === "members") {
      setStepIndex(2);
      runPreview();
    } else if (step === "preview") assign();
    else onClose();
  };

  const back = () => {
    if (assigning) return;
    if (step === "goals") onClose();
    else if (step === "members") setStepIndex(0);
    else if (step === "preview") {
      previewSeq.current += 1;
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      setStepIndex(1);
    }
  };

  const renderMembers = () => (
    <div>
      <label className={`mx-4 mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-terakkiBorder dark:border-white/[0.14]`}>
        <SearchIcon fontSize="small" className={cls.muted} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("bulk_search_member")}
          aria-label={t("bulk_search_member")}
          maxLength={60}
          className={`flex-1 min-w-0 bg-transparent outline-none text-sm ${cls.title} placeholder:text-terakkiNeutral dark:placeholder:text-white/45`}
        />
      </label>
      <div className="flex items-center justify-between gap-2 px-4 py-2.5">
        <span className={`text-sm ${cls.muted}`} data-testid="bulk-selected-count">
          {t("bulk_selected_count", { n: selected.length, total: pickable.length })}
        </span>
        <button
          type="button"
          disabled={visible.length === 0}
          onClick={() => setSelected((s) => toggleAllVisible(visible, s))}
          className={`text-sm ${cls.accent} disabled:opacity-50`}
        >
          {t(allVisibleSelected(visible, selected) ? "bulk_unselect_all" : "bulk_select_all")}
        </button>
      </div>
      {selected.length > MAX_BULK_MEMBERS ? (
        <p className={`px-4 pb-2 text-sm ${cls.danger}`}>{t("bulk_too_many_members", { n: MAX_BULK_MEMBERS })}</p>
      ) : null}
      {pickable.length === 0 ? (
        <p className={`px-4 py-2 text-sm ${cls.muted}`}>{t("bulk_no_members")}</p>
      ) : visible.length === 0 ? (
        <p className={`px-4 py-2 text-sm ${cls.muted}`}>{t("bulk_no_match")}</p>
      ) : (
        <ul className="border-t border-terakkiBorder dark:border-white/[0.14]">
          {visible.map((m) => {
            const on = selectedSet.has(m.id);
            return (
              <li key={m.id} className={cls.row}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  onClick={() => setSelected((s) => toggleMember(s, m.id))}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${cls.rowHover}`}
                >
                  {on ? (
                    <CheckCircleIcon fontSize="small" className={cls.accent} />
                  ) : (
                    <RadioButtonUncheckedIcon fontSize="small" className={cls.faint} />
                  )}
                  <span className={`text-[15px] truncate ${cls.title}`}>{memberName(m)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const renderPreview = () => {
    if (uncertain) return <p className={`px-4 text-sm ${cls.danger}`}>{t("bulk_uncertain")}</p>;
    if (previewLoading) {
      return (
        <div className="flex justify-center py-8">
          <CircularProgress size={24} />
        </div>
      );
    }
    if (previewError) {
      return (
        <div className="px-4 flex flex-col items-start gap-2">
          <p className={`text-sm ${cls.danger}`}>{previewError}</p>
          <SoftButton tone="soft" size="small" onClick={runPreview}>
            {t("retry")}
          </SoftButton>
        </div>
      );
    }
    if (!preview) return null;
    return (
      <>
        <Outcome data={preview} phase="preview" pickable={pickable} t={t} />
        {nobodyAssignable ? <p className={`px-4 pt-3 text-sm ${cls.muted}`}>{t("bulk_nobody_assignable")}</p> : null}
      </>
    );
  };

  const renderStep = () => {
    if (step === "goals") {
      return (
        <GoalListBuilder
          goals={goals}
          onChange={setGoals}
          templateId={templateId}
          onTemplateChange={setTemplateId}
        />
      );
    }
    if (step === "members") return renderMembers();
    if (step === "preview") return renderPreview();
    return result ? <Outcome data={result} phase="result" pickable={pickable} t={t} /> : null;
  };

  const isResult = step === "result";
  const green = step === "preview" || isResult;
  const primaryLabel = step === "preview" ? t("bulk_assign") : isResult ? t("bulk_done") : t("gw_next");

  const footer = uncertain ? (
    <SoftButton tone="soft" fullWidth onClick={onClose}>
      {t("bulk_back_to_group")}
    </SoftButton>
  ) : (
    <div className="flex gap-2">
      {!isResult ? (
        <SoftButton tone={stepIndex === 0 ? "cancel" : "soft"} fullWidth onClick={back} disabled={assigning}>
          {stepIndex === 0 ? t("manage_add_person_cancel") : t("gw_back")}
        </SoftButton>
      ) : null}
      <SoftButton tone={green ? "success" : "primary"} fullWidth onClick={next} disabled={!ready}>
        {assigning ? <CircularProgress size={18} color="inherit" /> : primaryLabel}
      </SoftButton>
    </div>
  );

  return (
    <Drawer
      open={open}
      // Atama sürerken kapanmaz.
      onClose={assigning ? undefined : onClose}
      width="sm"
      title={t("bulk_title")}
      subtitle={t("bulk_subtitle")}
      titleIcon={<PlaylistAddCheckIcon fontSize="small" />}
      footer={footer}
    >
      <StepDots count={BULK_STEPS.length} index={stepIndex} />
      <div className="px-4 pb-1">
        <h3 className={`text-lg font-medium ${cls.title}`}>{t(text.title)}</h3>
        <p className={`text-[13px] leading-5 ${cls.muted}`}>{t(text.description)}</p>
      </div>
      <div className="pb-6">{renderStep()}</div>
    </Drawer>
  );
}
